import { boot } from '@dazn/bootstrap-lib';
import {
    ChapterLoadEvent,
    addEventListener,
    triggerAppLaunchEvent,
    triggerChapterLoadEvent,
    triggerErrorImpressionEvent,
    triggerNativeAppReferralEvent,
    AddEventListenerCallback,
} from '@dazn/fe-events';
import { AppLaunchApplicationBrand } from '@dazn/fe-events/lib/generated/appLaunchEvent';
import {
    ChapterLoadUserType,
    ChapterLoadUserLoginStatus,
    ChapterLoadUserStatus,
} from '@dazn/fe-events/lib/generated/chapterLoadEvent';

import { BRANDS } from '../configuration/constants';

/**
 * Responsible for internal analytics event, like app launch, and error.
 */
interface CreateAnalyticsModuleProps {
    getViewerId: () => string;
    handleFeEvent: AddEventListenerCallback;
    getCountry: () => {
        billingCountry: boot.config.AnyCountry;
        contentCountry: boot.config.AnyCountry;
        geoCountry: boot.config.AnyCountry;
    };
    getCurrentChapterName: () => boot.config.ChapterName;
    getCurrentChapterVersion: () => string | undefined;
    getUserType: () => number;
    getUserStatus: () => boot.user.UserStatus | '';
    getAdvertisingDeviceId: () => Promise<string | null>;
    getBrandName: () => string;
    getSessionId: () => string;
}

const userTypeMap: ChapterLoadUserType[] = [
    'salesforce',
    'docomo',
    'dazn',
    'dazn2',
    'b2b',
];

export interface LaunchEventParams {
    initialURL: string;
    platform: string;
    model: string;
    applicationType: string;
    version: string;
    environment: boot.config.Environment;
    deviceId: string;
    launchParams: Record<string, unknown>;
    externalTrackingID: string;
    getBrandName: string;
}

export interface NativeAppReferralEventParams {
    platform: string;
    manufacturer: string;
    purchaseContextId: string;
    referralSource?: string;
    inAppSourceRef?: string;
    trackingId?: string;
    externalTrackingID?: string;
    productGroup?: string;
    entitlementSetId?: string;
    contextSource?: string;
    sourceDeviceId?: string;
    sourceSessionId?: string;
    deepLink?: string;
}

function createAnalyticsModule({
    getViewerId,
    handleFeEvent,
    getCountry,
    getCurrentChapterName,
    getCurrentChapterVersion,
    getUserType,
    getUserStatus,
    getAdvertisingDeviceId,
    getBrandName,
    getSessionId,
}: CreateAnalyticsModuleProps) {
    addEventListener(handleFeEvent);

    function getAnalyticsCommonData() {
        const sourceDeviceId = window?.localStorage.getItem('daznDeviceId');
        const sourceSessionId = window?.localStorage.getItem('daznSessionId');
        const userStatus = (getUserStatus()?.toLowerCase() ||
            'anonymous') as ChapterLoadUserStatus;
        const isAnonymous = userStatus === 'anonymous';
        const decodedAnonymousToken = isAnonymous
            ? window.dazn?.auth?.unstable_getDecodedAnonymousToken()
            : null;
        const daznUserId = isAnonymous
            ? decodedAnonymousToken?.user ?? ''
            : window.dazn?.auth?.unstable_getDecodedJWT()?.user ?? '';
        const profileId = getViewerId() ?? '';

        return {
            sourceDeviceId,
            sourceSessionId,
            userStatus,
            isAnonymous,
            decodedAnonymousToken,
            daznUserId,
            profileId,
        };
    }

    function sendLaunchEvent({
        initialURL,
        platform,
        model,
        applicationType,
        version,
        environment,
        deviceId,
        launchParams,
        externalTrackingID,
    }: LaunchEventParams) {
        return getAdvertisingDeviceId().then(idfa => {
            const { sourceDeviceId, sourceSessionId, daznUserId, profileId } =
                getAnalyticsCommonData();
            const daznSessionId = getSessionId();
            const isWebView = window?.dazn?.native?.isNative ? 'true' : 'false';

            triggerAppLaunchEvent({
                application: {
                    type: applicationType,
                    version,
                    environment,
                    device: {
                        model,
                        platform,
                    },
                    brand: getBrandName() as AppLaunchApplicationBrand,
                },
                clientId: deviceId,
                userAgent: window.navigator.userAgent,
                initialUrl: initialURL,
                launch: launchParams ?? {},
                idfa: idfa ?? '',
                externalTrackingID,
                isWebView,
                ...(daznSessionId && {
                    daznSessionId,
                }),
                ...(daznUserId && {
                    daznUserId,
                }),
                ...(profileId && {
                    profileId,
                }),
                ...(sourceDeviceId && {
                    sourceDeviceId,
                }),
                ...(sourceSessionId && {
                    sourceSessionId,
                }),
            });
        });
    }

    function sendChapterLoadEvent() {
        const currentChapterName = getCurrentChapterName();

        const {
            sourceDeviceId,
            sourceSessionId,
            userStatus,
            isAnonymous,
            decodedAnonymousToken,
            daznUserId,
            profileId,
        } = getAnalyticsCommonData();

        const userType: ChapterLoadUserType = userTypeMap[getUserType()];

        const userLoginStatus: ChapterLoadUserLoginStatus =
            userStatus !== 'anonymous' ? 'loggedin' : 'notloggedin';

        triggerChapterLoadEvent({
            chapter: currentChapterName as ChapterLoadEvent['chapter'],
            chapterVersion: getCurrentChapterVersion() || '',
            country: getCountry(),
            customerId: isAnonymous
                ? decodedAnonymousToken?.viewerId ?? ''
                : getViewerId() || '',
            ...(sourceDeviceId && {
                sourceDeviceId,
            }),
            ...(sourceSessionId && {
                sourceSessionId,
            }),
            user: {
                status: userStatus,
                type: getBrandName() === BRANDS.DAZN ? userType : '',
                loginStatus: userLoginStatus,
            },
            daznUserId,
            profileId,
        });
    }

    function sendNativeAppReferralEvent({
        platform,
        manufacturer,
        purchaseContextId,
        referralSource,
        inAppSourceRef,
        trackingId,
        externalTrackingID,
        productGroup,
        entitlementSetId,
        contextSource,
        sourceDeviceId,
        sourceSessionId,
        deepLink,
    }: NativeAppReferralEventParams) {
        triggerNativeAppReferralEvent({
            appReferral: {
                type: `${platform}-${manufacturer}`,
                purchaseContext: purchaseContextId,
                ...(referralSource && { referralSource }),
                ...(inAppSourceRef && { inAppSourceRef }),
                ...(trackingId && { trackingId }),
                ...(externalTrackingID && { externalTrackingID }),
            },
            ...(productGroup && { product: { productGroup } }),
            ...(entitlementSetId && { tier: { entitlementSetId } }),
            ...(contextSource && { contextSource }),
            ...(sourceDeviceId && { sourceDeviceId }),
            ...(sourceSessionId && { sourceSessionId }),
            ...(deepLink && { deepLink }),
        });
    }

    return {
        sendLaunchEvent,
        sendErrorEvent: triggerErrorImpressionEvent,
        sendChapterLoadEvent,
        sendNativeAppReferralEvent,
    };
}

export { createAnalyticsModule, CreateAnalyticsModuleProps };
